import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, MessageSquare, Bug, HelpCircle, Handshake } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const CATEGORIES = [
  { key: "Signaler un bug", Icon: Bug },
  { key: "Autres demandes", Icon: HelpCircle },
  { key: "Devenir partenaire", Icon: Handshake },
];

function timeLabel(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR") + " " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

export default function ContactAdmin() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(CATEGORIES[0].key);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef();

  const load = () => api.get("/messages").then((r) => setMessages(r.data)).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => {
    load();
    const t = setInterval(() => api.get("/messages").then((r) => setMessages(r.data)).catch(() => {}), 25000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      const r = await api.post("/messages", { text: t, category });
      setMessages((m) => [...m, r.data]);
      setText("");
      toast.success("Message envoyé à l'administrateur !");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-8 rounded-xl border border-[#00E676]/15 bg-[#131F17] p-6" data-testid="contact-admin">
      <h2 className="text-lg font-black flex items-center gap-2"><MessageSquare className="text-[#00E676]" size={18} /> Contacter l'administrateur</h2>
      <p className="text-sm text-[#A1B5A8] mt-1">Une question, un bug, un partenariat ? Écrivez-nous, nous vous répondrons ici.</p>

      <div className="mt-4 flex flex-wrap gap-2" data-testid="contact-categories">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setCategory(c.key)}
            data-testid={`contact-cat-${c.key}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${category === c.key ? "bg-[#00E676] text-[#04180a]" : "bg-[#0F1812] text-[#A1B5A8] border border-[#00E676]/20 hover:text-white"}`}
          >
            <c.Icon size={13} /> {c.key}
          </button>
        ))}
      </div>

      <div className="mt-4 max-h-80 overflow-y-auto space-y-3 rounded-lg bg-[#0F1812] border border-[#00E676]/10 p-4" data-testid="contact-thread">
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#00E676]" /></div>
        ) : messages.length === 0 ? (
          <p className="text-center text-sm text-[#697C6F] py-6">Aucun message pour l'instant. Envoyez votre premier message ci-dessous.</p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.sender === "user" ? "bg-gradient-to-r from-[#00E676] to-[#39FF14] text-[#04180a]" : "bg-[#1A2B20] text-white border border-[#00E676]/15"}`}>
                {m.sender === "admin" && <div className="text-[10px] font-black uppercase text-[#39FF14] mb-0.5">Administration</div>}
                {m.category && m.sender === "user" && <div className="text-[10px] font-black uppercase text-[#04180a]/70 mb-0.5">{m.category}</div>}
                <div className="whitespace-pre-wrap break-words">{m.text}</div>
                <div className={`text-[10px] mt-1 ${m.sender === "user" ? "text-[#04180a]/60" : "text-[#697C6F]"}`}>{timeLabel(m.created_at)}</div>
              </div>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>

      <div className="mt-3 flex items-end gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={`Votre message (${category})…`}
          rows={2}
          data-testid="contact-input"
          className="flex-1 bg-[#0F1812] border border-[#00E676]/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-[#697C6F] focus:outline-none focus:ring-2 focus:ring-[#39FF14] resize-none"
        />
        <button onClick={send} disabled={sending || !text.trim()} data-testid="contact-send"
          className="px-4 py-3 rounded-lg font-bold text-[#04180a] bg-gradient-to-r from-[#00E676] to-[#39FF14] disabled:opacity-50 flex items-center gap-2 shrink-0">
          {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
}
